/**
 * Make the above-the-fold hero image of a Builder PAGE entry LCP-eligible
 * (COM-181).
 *
 * Builder's own SDK `Image` block hardcodes `loading="lazy"` /
 * `fetchPriority="auto"` unless the block's `highPriority` option is set
 * (verified in `@builder.io/sdk-react` lib/edge image block:
 * `loading: e.highPriority ? "eager" : "lazy"`). Real homepage heroes are
 * authored out of those SDK-native blocks — NOT our custom hero components —
 * so with every image lazy, no LCP-eligible image exists and the LCP element
 * falls through to late-painting text (measured ~21s home LCP, LCP element =
 * the TrustArc banner copy).
 *
 * `prioritizeAboveFoldImages` is a deterministic, idempotent entry transform
 * that flips `component.options.highPriority = true` on the page's first
 * "hero-sized" image group and NOTHING else:
 *
 * 1. Walk the block tree in document order — regular `children` plus any
 *    `@builder.io/sdk:Element` nodes embedded in component options (Columns'
 *    `columns[].blocks`, Carousel's `slides`, Symbol content, …).
 * 2. Skip images that cannot be the LCP hero: blocks whose own natural
 *    dimensions (`options.width`/`height`) are both small, or whose own /
 *    ancestor `responsiveStyles` cap the rendered width to a small px value.
 *    Live homepages lead with strips of 24–96px icon Images before the hero;
 *    a naive "first image wins" flips those and leaves the hero lazy.
 * 3. The FIRST remaining (large) image wins, together with any other large
 *    Image blocks in the SAME parent array — heroes ship as display-toggled
 *    desktop/mobile sibling pairs, and whichever of the pair is visible must
 *    be eager.
 * 4. Propagate by asset URL: authors duplicate the hero asset (e.g. a static
 *    copy next to carousel `slides` embedding the same image), and which copy
 *    renders is decided by CSS we cannot see here. Same URL = one browser
 *    fetch, so flipping every block that references a winning URL is free.
 *
 * Everything below the fold stays lazy: selection stops at the first large
 * image group, so later sections are untouched.
 *
 * Runs as part of the shared `builder-page` SWR cache transform (see
 * `transformBuilderPageEntry`), so the SSR document, the SSR stash, and the
 * client's post-idle `/resources/builder-page-tree` fetch all see the SAME
 * flagged tree — server HTML and hydration agree on loading/fetchpriority.
 *
 * Mutates in place (same contract as `slimBuilderEntry`); no-op on
 * null/primitives, entries without image blocks, and entries whose images are
 * all small. A/B `variations` carry their own block trees and are processed
 * independently.
 *
 * `collectHeroImagePreloads` is the read-only second half: it turns the
 * flagged images into `<head>` preloads (COM-189) and, from the same walk,
 * elects a hero painted as a CSS `background-image` (COM-693). Homepage banners
 * are plain blocks carrying one asset per breakpoint in `responsiveStyles`, so
 * there is no `<img>` to flag — the only lever is a per-bucket preload.
 *
 * It runs on EVERY Builder page render, so the background election is
 * deliberately narrow. Per bucket, a block is a candidate only when it
 *
 * 1. sits in one of the first `HERO_BACKGROUND_TOP_BLOCKS` top-level blocks
 *    (or their descendants) — a footer CTA is not a hero;
 * 2. declares a hero-sized px `minHeight` / `height` for that bucket, and no
 *    small px `height` / `maxHeight` or width cap;
 * 3. is not decorative (a tiled or small-px-sized background is a texture);
 * 4. is certain to be rendered and styled by the SDK — it has an `id`, and
 *    neither it nor an ancestor is hidden, repeated, or bound on
 *    `hide` / `show`; its background is not bound; and it is not inside a
 *    `PersonalizationContainer`;
 * 5. comes before that bucket's image hero (shared `firstLargeVisibleImage`).
 *
 * The first candidate in document order wins the bucket. Every rule fails
 * towards NO background preload — the page then behaves as it did before
 * COM-693 — because a missed preload costs a later request, while a wrong one
 * spends high-priority bandwidth against the real LCP image.
 */
import { slimBuilderEntry } from "./builder-trim-references";
import {
  BACKGROUND_STYLE_KEYS,
  CSS_IMAGE_URL,
  rewriteBuilderMediaInPlace,
} from "./optimize-builder-media";

const ELEMENT_TYPE = "@builder.io/sdk:Element";

/**
 * An image capped (naturally or via styles) at or below this many px cannot
 * meaningfully be the LCP hero — the live homepages' pre-hero icon strips sit
 * at 24–100px, heroes at full/half viewport width (no px cap at all).
 */
const SMALL_IMAGE_MAX_PX = 250;

/**
 * A background hero must sit in one of the first N top-level blocks of the
 * entry (or inside one): room for two leading strips (an alert bar, an icon
 * row) ahead of the banner, without reaching below-the-fold promo sections.
 */
const HERO_BACKGROUND_TOP_BLOCKS = 3;

/**
 * The px `minHeight` / `height` a block must declare, in a bucket, to count as
 * a hero there. A block sized only by its content (`auto`, `%`, `fit-content`)
 * declares nothing we can trust, so it is not elected.
 */
const HERO_BACKGROUND_MIN_HEIGHT_PX = 300;

type Rec = Record<string, unknown>;

/**
 * Builder's responsive buckets: `large` is the authored default, `medium`
 * overrides at <=991px, `small` overrides at <=640px. Visibility must be
 * resolved per bucket — COM-189's 2026-08-27 regression was exactly this: the
 * homepage's first "large image group" was a desktop-only decorative pair
 * (`display: none` at <=991px), so the transform flagged two images that
 * render 0x0 on mobile and left the REAL mobile hero lazy. LCP fell back to
 * the consent banner on every run.
 */
const BUCKETS = ["small", "medium", "large"] as const;
type Bucket = (typeof BUCKETS)[number];
type BucketVisibility = Record<Bucket, boolean>;

const ALL_VISIBLE: BucketVisibility = {
  small: true,
  medium: true,
  large: true,
};

type FoundImage = {
  block: Rec;
  /** The children/blocks array this Image block sits in (sibling grouping). */
  siblings: unknown[];
  large: boolean;
  /** Static asset URL, when the block has one (bindings may omit it). */
  url: string | undefined;
  /** Document-order position, comparable with `FoundBackground.order`. */
  order: number;
  /** Per-bucket visibility, own display rules AND'd with every ancestor's. */
  visible: BucketVisibility;
};

/**
 * COM-693: a plain block (box / section / link) that paints a CSS
 * `background-image` from its `responsiveStyles`. Homepage banners are authored
 * this way — one asset per breakpoint on the SAME block — and the SDK emits
 * them as `.builder-<id> { background-image }` rules inside `@media` blocks,
 * which the browser's preload scanner cannot see.
 */
type FoundBackground = {
  /** Buckets where the block passes every hero bound (size, not decorative). */
  eligible: BucketVisibility;
  /** Document-order position, comparable with `FoundImage.order`. */
  order: number;
  /** Per-bucket visibility, own display rules AND'd with every ancestor's. */
  visible: BucketVisibility;
  /**
   * What the block paints in each bucket, after Builder's style cascade: a
   * URL, `null` when it declares a background with no electable image there
   * (`none`, a gradient, several layers), `undefined` when it declares nothing.
   */
  urls: Record<Bucket, string | null | undefined>;
};

/** Everything one document-order walk collects. */
type Found = { images: FoundImage[]; backgrounds: FoundBackground[] };

function isRecord(value: unknown): value is Rec {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isElement(value: unknown): value is Rec {
  return isRecord(value) && value["@type"] === ELEMENT_TYPE;
}

/** `"40px"` / `40` → 40; percentages, vw, calc(), etc. → null (not a cap). */
function toPx(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const match = /^\s*(\d+(?:\.\d+)?)px\s*$/.exec(value);
    if (match) return Number(match[1]);
  }
  return null;
}

/**
 * Smallest px `width`/`maxWidth` declared across the block's breakpoints.
 * Image election only — backgrounds use the per-bucket `bucketWidthCapPx`.
 */
function ownWidthCapPx(block: Rec): number | null {
  const responsiveStyles = block.responsiveStyles;
  if (!isRecord(responsiveStyles)) return null;
  let cap: number | null = null;
  for (const breakpoint of Object.values(responsiveStyles)) {
    if (!isRecord(breakpoint)) continue;
    for (const prop of ["maxWidth", "width"] as const) {
      const px = toPx(breakpoint[prop]);
      if (px !== null && (cap === null || px < cap)) cap = px;
    }
  }
  return cap;
}

function minCap(a: number | null, b: number | null): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return Math.min(a, b);
}

/**
 * Effective `display` per bucket, following Builder's cascade: `small`
 * inherits from `medium`, which inherits from `large` (the default styles).
 * Only an explicit `display: none` hides; every other/absent value is visible.
 */
function ownVisibility(block: Rec): BucketVisibility {
  const responsiveStyles = block.responsiveStyles;
  const displayIn = (bucket: Bucket): string | undefined => {
    if (!isRecord(responsiveStyles)) return undefined;
    const styles = responsiveStyles[bucket];
    if (!isRecord(styles)) return undefined;
    return typeof styles.display === "string" ? styles.display : undefined;
  };
  const large = displayIn("large");
  const medium = displayIn("medium") ?? large;
  const small = displayIn("small") ?? medium;
  return {
    small: small !== "none",
    medium: medium !== "none",
    large: large !== "none",
  };
}

function andVisibility(
  a: BucketVisibility,
  b: BucketVisibility,
): BucketVisibility {
  return {
    small: a.small && b.small,
    medium: a.medium && b.medium,
    large: a.large && b.large,
  };
}

/**
 * One style property per bucket, following Builder's cascade: `small` inherits
 * `medium`, which inherits `large`. `read` returns `undefined` for "this
 * bucket says nothing"; any other value (including `null`) stops the cascade.
 */
function cascade<T>(
  block: Rec,
  read: (styles: Rec) => T | undefined,
): Record<Bucket, T | undefined> {
  const responsiveStyles = block.responsiveStyles;
  const own = (bucket: Bucket): T | undefined => {
    if (!isRecord(responsiveStyles)) return undefined;
    const styles = responsiveStyles[bucket];
    return isRecord(styles) ? read(styles) : undefined;
  };
  const large = own("large");
  const mediumOwn = own("medium");
  const medium = mediumOwn === undefined ? large : mediumOwn;
  const smallOwn = own("small");
  const small = smallOwn === undefined ? medium : smallOwn;
  return { small, medium, large };
}

/** A usable declaration of `key`; empty strings are dropped by the browser. */
const declared =
  (key: string) =>
  (styles: Rec): string | number | undefined => {
    const value = styles[key];
    if (typeof value === "number") return value;
    return typeof value === "string" && value.trim() !== "" ? value : undefined;
  };

const BACKGROUND_KEYS: ReadonlySet<string> = new Set(BACKGROUND_STYLE_KEYS);

/**
 * What one bucket's own styles say about the background image: a URL, `null`
 * for "declares a background with no image we can elect", or `undefined` when
 * the bucket declares nothing and inherits.
 *
 * Reads the bag the way the browser reads the rule the SDK emits from it — own
 * keys in order, LAST declaration wins — so `backgroundImage: url(x)` followed
 * by `background: "#fff"` paints nothing, and the reverse paints `x`.
 *
 *  - An empty / whitespace value is an invalid declaration the browser drops:
 *    not declared, the cascade continues.
 *  - `none`, a colour or gradient shorthand, and the CSS-wide keywords
 *    (`inherit`, `unset`, `initial`, …) all replace an inherited image, and
 *    none of them names a URL: `null`.
 *  - More than one `url()` layer is `null` too. The browser fetches every
 *    layer and nothing here says which one is the banner.
 */
function declaredBackground(styles: Rec): string | null | undefined {
  let result: string | null | undefined;
  for (const key of Object.keys(styles)) {
    if (!BACKGROUND_KEYS.has(key)) continue;
    const value = styles[key];
    if (typeof value !== "string" || value.trim() === "") continue;
    const urls = [...value.matchAll(CSS_IMAGE_URL)];
    const layers = value.match(/url\(/gi)?.length ?? 0;
    result = urls.length === 1 && layers === 1 ? urls[0][2] : null;
  }
  return result;
}

/**
 * Effective background URL per bucket, following the same cascade as
 * `ownVisibility`: `small` inherits `medium`, which inherits `large`.
 *
 * The URL is read verbatim out of the style value. After
 * `rewriteBuilderMediaInPlace` it already carries the per-bucket
 * `?width=…&format=webp`, and the SDK writes the value into its `<style>`
 * unchanged — reading it instead of rebuilding it is what keeps the preload
 * `href` identical to the request the stylesheet makes.
 */
function ownBackgroundUrls(
  block: Rec,
): Record<Bucket, string | null | undefined> | null {
  const urls = cascade(block, declaredBackground);
  if (!urls.small && !urls.medium && !urls.large) return null;
  return urls;
}

/** Per-bucket px `width` / `maxWidth` cap — each resolved through the cascade. */
function bucketWidthCapPx(block: Rec): Record<Bucket, number | null> {
  const width = cascade(block, declared("width"));
  const maxWidth = cascade(block, declared("maxWidth"));
  const cap = (bucket: Bucket) =>
    minCap(toPx(width[bucket]), toPx(maxWidth[bucket]));
  return { small: cap("small"), medium: cap("medium"), large: cap("large") };
}

function minBucketCaps(
  a: Record<Bucket, number | null>,
  b: Record<Bucket, number | null>,
): Record<Bucket, number | null> {
  return {
    small: minCap(a.small, b.small),
    medium: minCap(a.medium, b.medium),
    large: minCap(a.large, b.large),
  };
}

/** `repeat`, `repeat-x`, `repeat no-repeat`, … — but not `no-repeat`. */
function isTiled(backgroundRepeat: unknown): boolean {
  return (
    typeof backgroundRepeat === "string" &&
    backgroundRepeat
      .split(/[\s,]+/)
      .some(token => /^(repeat(-[xy])?|round|space)$/i.test(token))
  );
}

/** `40px`, `auto 40px`, `40px 40px` — a px size too small to be a banner. */
function isSmallBackgroundSize(backgroundSize: unknown): boolean {
  if (typeof backgroundSize !== "string") return false;
  return [...backgroundSize.matchAll(/(\d+(?:\.\d+)?)px/g)].some(
    match => Number(match[1]) <= SMALL_IMAGE_MAX_PX,
  );
}

/**
 * Whether a `background` shorthand value declares a tiled or small-px-sized
 * image. Only the part after `/` is a size — px values before it are a
 * position. `url(…)` is dropped first so a path cannot read as a keyword.
 */
function isDecorativeShorthand(background: unknown): boolean {
  if (typeof background !== "string") return false;
  const value = background.replace(/url\([^)]*\)/gi, " ");
  if (isTiled(value)) return true;
  return value
    .split(",")
    .some(layer => isSmallBackgroundSize(layer.split("/")[1]));
}

/**
 * COM-693: the buckets in which a background-painting block is plausibly the
 * hero banner — see the module doc for the rule set. `widthCapPx` is the
 * block's own per-bucket cap already AND'd with every ancestor's, so a column
 * that is 240px on desktop and 100% on mobile stays eligible on mobile.
 */
function heroBackgroundBuckets(
  block: Rec,
  widthCapPx: Record<Bucket, number | null>,
): BucketVisibility {
  const height = cascade(block, declared("height"));
  const minHeight = cascade(block, declared("minHeight"));
  const maxHeight = cascade(block, declared("maxHeight"));
  const repeat = cascade(block, declared("backgroundRepeat"));
  const size = cascade(block, declared("backgroundSize"));
  const shorthand = cascade(block, declared("background"));
  const isSmallPx = (value: unknown): boolean => {
    const px = toPx(value);
    return px !== null && px <= SMALL_IMAGE_MAX_PX;
  };
  const eligibleIn = (bucket: Bucket): boolean => {
    const cap = widthCapPx[bucket];
    if (cap !== null && cap <= SMALL_IMAGE_MAX_PX) return false;
    if (isSmallPx(height[bucket]) || isSmallPx(maxHeight[bucket])) return false;
    if (isTiled(repeat[bucket]) || isSmallBackgroundSize(size[bucket])) {
      return false;
    }
    if (isDecorativeShorthand(shorthand[bucket])) return false;
    const tallest = Math.max(
      toPx(minHeight[bucket]) ?? 0,
      toPx(height[bucket]) ?? 0,
    );
    return tallest >= HERO_BACKGROUND_MIN_HEIGHT_PX;
  };
  return {
    small: eligibleIn("small"),
    medium: eligibleIn("medium"),
    large: eligibleIn("large"),
  };
}

/** Mirror of the SDK block's `canShowBlock`: `show && !hide`, by truthiness. */
function isHidden(block: Rec): boolean {
  if ("hide" in block && block.hide) return true;
  return "show" in block && !block.show;
}

function hasBinding(block: Rec, pattern: RegExp): boolean {
  const bindings = block.bindings;
  return isRecord(bindings) && Object.keys(bindings).some(k => pattern.test(k));
}

/**
 * COM-693: whether the SDK may not render this block — and therefore anything
 * inside it — at all. Hidden blocks render nothing, a `repeat.collection`
 * renders once per item of a runtime collection (possibly zero), and a bound
 * `hide` / `show` is decided by an expression we cannot evaluate here.
 */
function mayNotRender(block: Rec): boolean {
  if (isHidden(block)) return true;
  const repeat = block.repeat;
  if (isRecord(repeat) && repeat.collection) return true;
  return hasBinding(block, /^(hide|show)$/);
}

/**
 * COM-693: whether the background this block paints is the one its
 * `responsiveStyles` declare. Without an `id` the SDK emits no CSS for the
 * block at all (`BlockStyles` returns "" for a falsy class name), and a
 * binding on a background key rewrites the value at render time. Same spirit
 * as the "leave bound blocks alone" guard in `builder-ssr-styles.ts`.
 */
function paintsDeclaredBackground(block: Rec): boolean {
  if (typeof block.id !== "string" || !block.id) return false;
  return !hasBinding(block, /^(style|responsiveStyles\.[^.]+)\.background/i);
}

/**
 * Renders one of several `variants` chosen per shopper at render time, so no
 * background inside it is known to be the one painted.
 */
const PERSONALIZATION_COMPONENT = "PersonalizationContainer";

function isImageBlock(block: Rec): boolean {
  const component = block.component;
  return isRecord(component) && component.name === "Image";
}

function imageOptions(block: Rec): Rec | null {
  const component = block.component;
  if (!isRecord(component)) return null;
  const options = component.options;
  return isRecord(options) ? options : null;
}

function isLargeImage(block: Rec, ancestorCapPx: number | null): boolean {
  const options = imageOptions(block);
  const naturalWidth = toPx(options?.width);
  const naturalHeight = toPx(options?.height);
  if (
    naturalWidth !== null &&
    naturalHeight !== null &&
    naturalWidth <= SMALL_IMAGE_MAX_PX &&
    naturalHeight <= SMALL_IMAGE_MAX_PX
  ) {
    return false;
  }
  const cap = minCap(ownWidthCapPx(block), ancestorCapPx);
  return cap === null || cap > SMALL_IMAGE_MAX_PX;
}

/** What a subtree inherits from its ancestors during the walk. */
type WalkContext = {
  /** Image election: smallest px width cap across ALL buckets (unchanged). */
  capPx: number | null;
  /** Background election: the px width cap in each bucket. */
  bucketCapPx: Record<Bucket, number | null>;
  visible: BucketVisibility;
  /** Index of the top-level block this subtree belongs to; null at the root. */
  topIndex: number | null;
  /**
   * False once an ancestor may not render or the subtree is personalised —
   * backgrounds below are not recorded. Images are still collected: their
   * election predates COM-693 and is deliberately left as it was.
   */
  backgrounds: boolean;
};

/**
 * Document-order walk over an array of sibling elements. Component options
 * are scanned before `children` — embedded option blocks (Columns / Carousel
 * content) render inside the component, children render within/after it.
 */
function walkSiblings(
  siblings: unknown[],
  context: WalkContext,
  found: Found,
): void {
  siblings.forEach((node, index) => {
    if (!isRecord(node)) return;
    const topIndex = context.topIndex ?? index;
    const visible = andVisibility(context.visible, ownVisibility(node));
    const order = found.images.length + found.backgrounds.length;
    const capPx = minCap(ownWidthCapPx(node), context.capPx);
    // Background bookkeeping only where a background can still be elected.
    const tracked =
      context.backgrounds && topIndex < HERO_BACKGROUND_TOP_BLOCKS;
    const bucketCapPx = tracked
      ? minBucketCaps(bucketWidthCapPx(node), context.bucketCapPx)
      : context.bucketCapPx;
    const rendered = tracked && !mayNotRender(node);
    if (isImageBlock(node)) {
      const url = imageOptions(node)?.image;
      found.images.push({
        block: node,
        siblings,
        large: isLargeImage(node, context.capPx),
        url: typeof url === "string" ? url : undefined,
        order,
        visible,
      });
    } else if (rendered && paintsDeclaredBackground(node)) {
      const urls = ownBackgroundUrls(node);
      if (urls) {
        found.backgrounds.push({
          eligible: heroBackgroundBuckets(node, bucketCapPx),
          order,
          visible,
          urls,
        });
      }
    }
    const component = node.component;
    const inner: WalkContext = {
      capPx,
      bucketCapPx,
      visible,
      topIndex,
      backgrounds:
        rendered &&
        !(isRecord(component) && component.name === PERSONALIZATION_COMPONENT),
    };
    if (isRecord(component)) {
      scanForEmbeddedElements(component.options, inner, found);
    }
    const children = node.children;
    if (Array.isArray(children)) {
      walkSiblings(children, inner, found);
    }
  });
}

/**
 * Recurse through plain option objects/arrays looking for embedded
 * `@builder.io/sdk:Element` nodes (Carousel `slides`, Columns'
 * `columns[].blocks`, Symbol content blocks, …). Elements found in the same
 * array are treated as siblings.
 */
function scanForEmbeddedElements(
  value: unknown,
  context: WalkContext,
  found: Found,
): void {
  if (Array.isArray(value)) {
    if (value.some(isElement)) {
      walkSiblings(value, context, found);
      return;
    }
    for (const item of value) {
      scanForEmbeddedElements(item, context, found);
    }
    return;
  }
  if (!isRecord(value)) return;
  if (isElement(value)) {
    walkSiblings([value], context, found);
    return;
  }
  for (const nested of Object.values(value)) {
    scanForEmbeddedElements(nested, context, found);
  }
}

function walkBlocks(blocks: unknown[], backgrounds: boolean): Found {
  const found: Found = { images: [], backgrounds: [] };
  walkSiblings(
    blocks,
    {
      capPx: null,
      bucketCapPx: { small: null, medium: null, large: null },
      visible: ALL_VISIBLE,
      topIndex: null,
      backgrounds,
    },
    found,
  );
  return found;
}

/**
 * A bucket's image hero: the first large image in document order that is
 * visible there. ONE definition, shared by `prioritizeBlocks` (which flags it)
 * and the background election (which must yield to it) so the two cannot
 * disagree about where the image hero is.
 */
function firstLargeVisibleImage(
  images: FoundImage[],
  bucket: Bucket,
): FoundImage | undefined {
  return images.find(image => image.large && image.visible[bucket]);
}

function setHighPriority(block: Rec): void {
  const component = block.component;
  if (!isRecord(component)) return;
  const options = isRecord(component.options) ? component.options : {};
  component.options = options;
  options.highPriority = true;
}

/**
 * Minimum rendered height, in px, for the flagged hero on narrow viewports.
 *
 * COM-189. LCP is a LARGEST-element competition, and the hero was losing it on
 * area alone. Measured on the FSA homepage at a 412px viewport:
 *
 *   hero as shipped   396 x 149  = 58,608 px^2   painted at 3,311 ms
 *   consent banner p  392 x 176  = 67,056 px^2   painted at 3,729 ms
 *
 * The hero painted FIRST and still lost, so LCP resolved to third-party
 * consent text whose own render delay is ~91% of LCP. Giving the hero block a
 * 190px floor takes it to 75,042 px^2 and the LCP element flips to the hero —
 * verified in-browser via PerformanceObserver, control vs treatment.
 *
 * Why 190 and not 170: the threshold is `bannerArea / heroWidth` ≈ 169px, and
 * the banner's area is third-party text that changes with copy length and
 * locale. 190 buys ~12% headroom. This is the fragile part of the fix — if
 * TrustArc lengthens its notice, the banner grows and can retake LCP. Re-run
 * `scripts/lcp-flip-test.mjs` before assuming it still holds.
 *
 * It is also defensible on design grounds independent of the metric: 149px is
 * 18% of an 823px viewport, which is a strip rather than a hero. 190px is 23%.
 * The block uses `object-fit: cover`, so the extra height crops rather than
 * distorts — but it IS a visible change and needs design sign-off before it
 * reaches a shopper tier.
 */
const HERO_MIN_HEIGHT_PX = 190;

/**
 * Builder responsive-style buckets that map to narrow viewports. `small` is the
 * <=640px bucket that the FSA homepage hero actually renders in; `medium`
 * (<=991px) is included so tablets do not fall back to the unfloored height.
 */
const NARROW_STYLE_BUCKETS = ["small", "medium"] as const;

/**
 * Give the flagged hero a min-height on narrow viewports so it out-areas the
 * consent banner and becomes the LCP element.
 *
 * Applied ONLY to blocks `prioritizeBlocks` already chose, so below-fold images
 * and chrome are untouched. Never lowers an authored value — if a block already
 * asks for more height, that wins.
 *
 * Two guards added 2026-08-27, both measured (scripts/lcp-flip-test.mjs against
 * the live FSA homepage):
 *
 *  1. Bucket visibility. Flooring a block that is `display: none` in that
 *     bucket does nothing — the 08-27 content window had the floor sitting on
 *     two hidden desktop-only images while the banner won LCP 17/17 runs.
 *  2. `object-fit`. The floor grows the element BOX, but for a `contain` image
 *     LCP counts the letterboxed IMAGE, not the box: measured box 300x240 =
 *     72,000 px^2 while the LCP entry stayed size=56,100 and the banner
 *     (67,056) kept the record. Only `cover` (the SDK default —
 *     `objectFit: options.backgroundSize || "cover"`) paints the whole box, so
 *     only cover heroes get the floor. Flooring a contain hero is pure
 *     letterbox whitespace: a visible design change with zero LCP effect.
 */
function setHeroMinHeight(block: Rec, visible: BucketVisibility): void {
  const fit = imageOptions(block)?.backgroundSize;
  if (fit === "contain") return;

  const existing = block.responsiveStyles;
  const responsiveStyles = isRecord(existing) ? existing : {};
  block.responsiveStyles = responsiveStyles;

  for (const bucket of NARROW_STYLE_BUCKETS) {
    if (!visible[bucket]) continue;
    const current = isRecord(responsiveStyles[bucket])
      ? (responsiveStyles[bucket] as Rec)
      : {};
    responsiveStyles[bucket] = current;
    // Respect an authored floor that is already taller.
    const authored = toPx(current.minHeight);
    if (authored !== null && authored >= HERO_MIN_HEIGHT_PX) continue;
    current.minHeight = `${HERO_MIN_HEIGHT_PX}px`;
  }
}

function prioritizeBlocks(blocks: unknown): void {
  if (!Array.isArray(blocks) || blocks.length === 0) return;
  const { images: found } = walkBlocks(blocks, false);

  // Per-BUCKET selection (2026-08-27). "First large image in document order"
  // is wrong the moment the first group is hidden at some breakpoint: the
  // 08-27 homepage led with a desktop-only decorative pair, so mobile shipped
  // with NO prioritised hero at all — the real mobile hero (nested one column
  // deeper) stayed `loading="lazy"`. Each bucket now elects its own first
  // large VISIBLE image (plus its large visible siblings in the same parent
  // array — display-toggled pairs); the union across buckets gets flagged.
  const winners = new Set<FoundImage>();
  for (const bucket of BUCKETS) {
    const first = firstLargeVisibleImage(found, bucket);
    if (!first) continue;
    for (const image of found) {
      if (
        image.large &&
        image.visible[bucket] &&
        image.siblings === first.siblings
      ) {
        winners.add(image);
      }
    }
  }
  if (winners.size === 0) return;

  const winningUrls = new Set(
    [...winners].map(image => image.url).filter((url): url is string => !!url),
  );

  for (const image of found) {
    if (
      winners.has(image) ||
      (image.url !== undefined && winningUrls.has(image.url))
    ) {
      setHighPriority(image.block);
      // COM-189: eager+high only controls WHEN it loads. Winning LCP is an
      // area contest, and the hero was losing it to the consent banner.
      setHeroMinHeight(image.block, image.visible);
    }
  }
}

/**
 * Flip the SDK Image block's `highPriority` option on the entry's first
 * above-the-fold image group so it SSRs `loading="eager"
 * fetchpriority="high"`. See the module doc for selection rules. Mutates in
 * place; returns the entry for transform chaining.
 */
export function prioritizeAboveFoldImages<T>(entry: T): T {
  if (!entry || typeof entry !== "object") return entry;
  const record = entry as Rec;
  const data = record.data;
  if (isRecord(data)) {
    prioritizeBlocks(data.blocks);
  }
  // A/B test variations ship their own block trees; whichever one the SDK
  // renders must carry the same above-the-fold flags.
  const variations = record.variations;
  if (isRecord(variations)) {
    for (const variation of Object.values(variations)) {
      if (isRecord(variation) && isRecord(variation.data)) {
        prioritizeBlocks(variation.data.blocks);
      }
    }
  }
  return entry;
}

/**
 * THE shared `builder-page` SWR cache transform: slim (drop editor metadata +
 * referenced-page blocks), rewrite Builder CDN urls (webp + width), then flag
 * the above-the-fold hero image group (COM-181).
 *
 * Both `builder-page` call sites — the catch-all document loader
 * (`~/routes/$.tsx`) and the post-idle tree resource
 * (`~/routes/resources/builder-page-tree.ts`) — MUST use this same function:
 * they share one SWR cache entry, and the SSR'd document HTML has to agree
 * with the tree the client re-renders from, or the hero flips back to lazy
 * (or worse, hydration diverges) after idle.
 *
 * Chrome entries (header/footer in `layout.tsx`) intentionally keep plain
 * `slimBuilderEntry`: their first image is a nav/logo asset, and
 * fetchpriority=high on chrome would compete with the page's real hero.
 */
export function transformBuilderPageEntry<T>(entry: T): T {
  return prioritizeAboveFoldImages(
    rewriteBuilderMediaInPlace(slimBuilderEntry(entry)),
  );
}

/* ------------------------------------------------------------------------- *
 * COM-189: <head> preload for the prioritised hero.
 * ------------------------------------------------------------------------- */

/** What a caller needs to render one `<link rel="preload" as="image">`. */
export type HeroImagePreload = {
  /**
   * For an Image block: the fallback `href`, identical to the `<img src>` the
   * SDK renders. For a background hero (COM-693): the URL inside the block's
   * `background-image: url(…)`, and the only candidate.
   */
  href: string;
  /**
   * webp candidates, identical to the SDK's `<source srcSet>`. Absent for
   * background heroes and for `noWebp` images — the page requests exactly
   * `href` for those.
   */
  imageSrcSet?: string;
  /**
   * Breakpoint at which this half of a display-toggled hero pair is visible.
   * Without it, mobile spends high-priority bandwidth preloading the desktop
   * half, which renders 0x0 and still downloads. Undefined = always visible.
   */
  media?: string;
};

/**
 * The width ladder the SDK's image block hardcodes. Mirrored, not imported —
 * it is a literal inside the bundled block with no export.
 */
const SDK_SRCSET_WIDTHS = [100, 200, 400, 800, 1200, 1600, 2000] as const;

/** Mirror of the SDK's `v(url, "width", n)` param rewrite. */
function replaceWidthParam(url: string, width: number): string {
  const re = /([?&])width=.*?(&|$)/i;
  if (re.test(url)) return url.replace(re, `$1width=${width}$2`);
  return `${url}${url.includes("?") ? "&" : "?"}width=${width}`;
}

/**
 * Mirror of the SDK image block's `f()` (srcset) composed with `t()` (the webp
 * `<source>` rewrite).
 *
 * ⚠️ This is a HAND-MAINTAINED REPLICA of third-party code, and it has to be
 * byte-exact. A preload only satisfies the real request when the URL STRING
 * matches; a mismatch does not fail loudly, it silently downloads the hero
 * TWICE — worse than not preloading at all.
 *
 * Two details that are easy to get wrong, both load-bearing:
 *
 *  1. Param ORDER is `?format=webp&width=N`, not `?width=N&format=webp`. The
 *     SDK reaches it by building `?width=N` first and then running
 *     `url.replace(/\?/g, "?format=webp&")`, so `format` lands first. Our own
 *     `builderImageUrl` helper produces the opposite order — do NOT use it here.
 *  2. Candidate ORDER puts the URL's own declared width LAST (the SDK does
 *     `.filter(c => c !== n).concat([declared])`). Order does not change
 *     selection, but it does change the string, and pinning the exact string
 *     is how the test catches SDK drift on upgrade.
 *
 * `rewriteImageOptions` guarantees `options.image` is a bare `?width=`, which
 * is what makes the SDK's `Number(url.split("?width=")[1])` parse — see
 * `optimize-builder-media.ts`.
 */
export function sdkWebpSrcSet(imageUrl: string): string | undefined {
  if (!/builder\.io/.test(imageUrl)) return undefined;
  const declared = Number(imageUrl.split("?width=")[1]);
  const hasDeclared = Number.isFinite(declared);
  const entries = SDK_SRCSET_WIDTHS.filter(w => w !== declared).map(
    w => `${replaceWidthParam(imageUrl, w)} ${w}w`,
  );
  if (hasDeclared) entries.push(`${imageUrl} ${declared}w`);
  if (entries.length === 0) return undefined;
  return entries.join(", ").replace(/\?/g, "?format=webp&");
}

/**
 * Collect preload descriptors for every image `prioritizeAboveFoldImages`
 * flagged `highPriority`.
 *
 * Why this is needed even though the hero is already `eager` +
 * `fetchpriority=high`: on the FSA homepage the flagged `<img>` sits at byte
 * ~161,300 of a 1.6 MB document, so the browser cannot discover it until it has
 * parsed 161 KB of HTML. `eager` only says "do not defer once you find me".
 *
 * Deliberately returns ALL flagged blocks rather than just the first. The
 * transform flags display-toggled desktop/mobile sibling pairs on purpose
 * (only one is visible, and which one is decided by CSS this code cannot see),
 * so preloading only one would miss on whichever breakpoint the shopper is on.
 * `media` keeps each preload to the breakpoint that will actually request it.
 */
export function collectHeroImagePreloads(entry: unknown): HeroImagePreload[] {
  if (!entry || typeof entry !== "object") return [];
  const record = entry as Rec;
  const data = record.data;
  if (!isRecord(data) || !Array.isArray(data.blocks)) return [];

  const { images: found, backgrounds } = walkBlocks(data.blocks, true);

  // The same asset can be flagged in several blocks (URL propagation across
  // duplicated hero copies). One preload per href, visible wherever ANY of its
  // blocks is visible — so the union of their bucket visibility decides
  // `media`.
  const byHref = new Map<
    string,
    { noWebp: boolean; visible: BucketVisibility }
  >();
  for (const image of found) {
    const options = imageOptions(image.block);
    if (!options || options.highPriority !== true) continue;
    const href = typeof options.image === "string" ? options.image : undefined;
    if (!href) continue;
    const noWebp = Boolean(options.noWebp);
    const existing = byHref.get(href);
    if (existing) {
      // Copies of one asset can disagree; a srcset preload is only safe when
      // EVERY copy renders one, so any `noWebp` copy wins.
      existing.noWebp ||= noWebp;
      existing.visible = {
        small: existing.visible.small || image.visible.small,
        medium: existing.visible.medium || image.visible.medium,
        large: existing.visible.large || image.visible.large,
      };
    } else {
      byHref.set(href, { noWebp, visible: image.visible });
    }
  }

  // COM-693: background heroes first — they are the banner, and by
  // construction they precede the image hero in the document.
  const out: HeroImagePreload[] = collectHeroBackgroundPreloads(
    record,
    electHeroBackgrounds(found, backgrounds),
  );
  for (const [href, { noWebp, visible }] of byHref) {
    // Hidden at every breakpoint: preloading it is a pure bandwidth tax.
    if (!visible.small && !visible.medium && !visible.large) continue;
    // Already preloaded as a background hero; React keys image preloads by
    // href, so a second call could only disagree about `media`.
    if (out.some(preload => preload.href === href)) continue;
    // An authored `noWebp` makes the SDK render a bare `<img src>` — no
    // `srcSet`, no webp `<source>` — so the preload is the plain `href`; a
    // srcset preload would fetch a webp candidate the page never requests.
    //
    // No `imageSizes` either: the candidates are the SDK's webp
    // `<source srcSet>`, and the SDK puts the block's `sizes` on the `<img>`
    // only, so the `<source>` is sized at 100vw.
    out.push({
      href,
      imageSrcSet: noWebp ? undefined : sdkWebpSrcSet(href),
      media: mediaForVisibility(visible),
    });
  }
  return out;
}

/**
 * COM-693: preloads for a hero painted as a CSS `background-image`.
 *
 * The homepage banner is a plain block whose `responsiveStyles` carry a
 * different asset per breakpoint. The SDK ships those as rules in the inline
 * `<style>`, so the request cannot start until the stylesheet is applied to a
 * parsed element — measured at DOMContentLoaded on an ~846 KB document, for the
 * element that IS the mobile LCP.
 *
 * Selection, per bucket: the first block in document order that passes the
 * hero bounds (`heroBackgroundBuckets`; near the top, rendered and unbound —
 * see the module doc), is visible and paints a background URL in that bucket —
 * but only when it comes BEFORE that bucket's image hero (or there is none).
 * A block that fails a bound is skipped, not a stopper: a tiled wrapper
 * texture around the banner does not take the banner's slot. A block that
 * passes them but declares no image in the bucket (`background-image: none`)
 * IS a stopper: the banner paints nothing there, and a later section must not
 * inherit its slot. The image election in `prioritizeBlocks` is deliberately
 * left alone: a background that follows the image hero is below it, and
 * un-flagging an `<img>` hero because of a decorative background would reopen
 * the 2026-08-27 regression class.
 */
function electHeroBackgrounds(
  images: FoundImage[],
  backgrounds: FoundBackground[],
): Record<Bucket, string | undefined> {
  const elected: Record<Bucket, string | undefined> = {
    small: undefined,
    medium: undefined,
    large: undefined,
  };
  for (const bucket of BUCKETS) {
    const hero = backgrounds.find(
      background =>
        background.eligible[bucket] &&
        background.visible[bucket] &&
        background.urls[bucket] !== undefined,
    );
    const url = hero?.urls[bucket];
    if (!hero || !url) continue;
    const imageHero = firstLargeVisibleImage(images, bucket);
    if (imageHero && imageHero.order < hero.order) continue;
    elected[bucket] = url;
  }
  return elected;
}

/**
 * One preload per distinct elected URL, scoped by `media` to exactly the
 * buckets where that URL is the one painted, so each viewport fetches one
 * banner and the stylesheet's own request is served from the preload.
 *
 * No preload at all when the entry carries custom `meta.breakpoints`: the SDK
 * then emits its `@media` rules at those widths, and the bucket edges used for
 * `media` here are the defaults. And none in a bucket where an A/B variation
 * elects a different banner — which tree a shopper gets is not known here.
 */
function collectHeroBackgroundPreloads(
  entry: Rec,
  elected: Record<Bucket, string | undefined>,
): HeroImagePreload[] {
  const meta = entry.meta;
  if (isRecord(meta) && meta.breakpoints) return [];

  const variations = entry.variations;
  if (isRecord(variations)) {
    for (const variation of Object.values(variations)) {
      const blocks =
        isRecord(variation) && isRecord(variation.data)
          ? variation.data.blocks
          : undefined;
      const found = walkBlocks(Array.isArray(blocks) ? blocks : [], true);
      const theirs = electHeroBackgrounds(found.images, found.backgrounds);
      for (const bucket of BUCKETS) {
        if (theirs[bucket] !== elected[bucket]) elected[bucket] = undefined;
      }
    }
  }

  const bucketsByUrl = new Map<string, BucketVisibility>();
  for (const bucket of BUCKETS) {
    const url = elected[bucket];
    if (!url) continue;
    const buckets = bucketsByUrl.get(url) ?? {
      small: false,
      medium: false,
      large: false,
    };
    buckets[bucket] = true;
    bucketsByUrl.set(url, buckets);
  }

  const out: HeroImagePreload[] = [];
  for (const [href, buckets] of bucketsByUrl) {
    out.push({ href, media: mediaForBackgroundBuckets(buckets) });
  }
  return out;
}

/**
 * `mediaForVisibility`, except that the one non-contiguous set gets a real
 * media query list instead of "always". For an `<img>` an unscoped preload is
 * merely conservative; for a background it would fetch a banner the middle
 * bucket never paints, at high priority, next to the one it does.
 */
function mediaForBackgroundBuckets(
  buckets: BucketVisibility,
): string | undefined {
  if (buckets.small && !buckets.medium && buckets.large) {
    return `(max-width: 640px), ${ABOVE_MEDIUM}`;
  }
  return mediaForVisibility(buckets);
}

/**
 * The lower edge of the `medium` and `large` buckets.
 *
 * Viewport widths are not integers: page zoom and fractional device pixel
 * ratios produce widths like 640.5px, where the SDK's `(max-width: 640px)`
 * rule is already off but a `(min-width: 641px)` preload is not on yet. `.02px`
 * closes that gap and, unlike `not all and (max-width: 640px)`, can be and-ed
 * with the middle bucket's upper bound.
 */
const ABOVE_SMALL = "(min-width: 640.02px)";
const ABOVE_MEDIUM = "(min-width: 991.02px)";

/**
 * `media` for a hero preload, derived from the image's per-bucket visibility —
 * its own `display` rules AND every ancestor's (2026-08-27: the hidden halves
 * of the hero were hidden by their own styles, but a container-level
 * `display: none` must scope the preload just the same).
 *
 * Why this exists: measured on the FSA homepage at 412px, a desktop-only half
 * renders **0 x 0 and still downloads** (Chrome fetches images inside
 * `display: none`), so an unscoped preload spends high-priority bandwidth on an
 * image that is never shown, competing with the one that is.
 *
 * Builder's buckets are defined by the SDK's own rules, `@media (max-width:
 * 991px)` and `@media (max-width: 640px)`: `small` is <=640px, `medium` is
 * everything above 640px up to 991px, `large` everything above 991px.
 * Returns the media query covering exactly the buckets where the image is
 * visible, when that set is a contiguous range. Visible everywhere (or a
 * non-contiguous set, which authors cannot express in the Builder UI) →
 * undefined, i.e. preload unconditionally — deliberately conservative, never
 * suppressing the preload of a visible hero.
 */
function mediaForVisibility(visible: BucketVisibility): string | undefined {
  const { small, medium, large } = visible;
  if (small && medium && large) return undefined;
  if (!small && medium && large) return ABOVE_SMALL;
  if (!small && !medium && large) return ABOVE_MEDIUM;
  if (small && !medium && !large) return "(max-width: 640px)";
  if (small && medium && !large) return "(max-width: 991px)";
  if (!small && medium && !large)
    return `${ABOVE_SMALL} and (max-width: 991px)`;
  // small+large but not medium: not expressible as one range — no media.
  return undefined;
}
